import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import Home from './pages/Home';
import ZodiacPage from './pages/ZodiacPage';
import LifeProgress from './pages/LifeProgress';
import PersonalityPage from './pages/PersonalityPage';
import TarotPage from './pages/TarotPage';
import TimelinePage from './pages/TimelinePage';
import ChineseZodiacPage from './pages/ChineseZodiacPage';
import GlossaryPage from './pages/GlossaryPage';
import ResourcesPage from './pages/ResourcesPage';
import SitemapPage from './pages/SitemapPage';
import GettingStartedPage from './pages/GettingStartedPage';
import FAQPage from './pages/FAQPage';
import MethodologyToolsPage from './pages/MethodologyToolsPage';
import BlogIndex from './pages/BlogIndex';
import BlogPost from './pages/BlogPost';
import { PrivacyPolicy, About, Contact, TermsOfService, EditorialPolicy } from './pages/Legal';
import { UserData, AppSettings } from './types';
import CookieConsent from './components/CookieConsent';
import { recordPageView, initInteractionWatch } from './utils/adGuard';
import { DEFAULT_LIFE_EXPECTANCY, ZODIAC_THEMES, MOOD_THEMES } from './constants';
import { getInitialSettings, saveSettings, getCurrentSeasonSign } from './utils/cosmic';
import { initAudio } from './utils/sound';

const App: React.FC = () => {
  // History API routing state
  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/');
  
  // Global State for User Data
  const [userData, setUserData] = useState<UserData>(() => {
    // Try load from local storage
    const saved = localStorage.getItem('zodiage_user');
    if (saved) {
        const parsed = JSON.parse(saved);
        return {
            ...parsed,
            birthDate: parsed.birthDate ? new Date(parsed.birthDate) : null,
            timezone: parsed.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }
    return {
        birthDate: null,
        includeTime: false,
        birthPlace: '',
        coordinates: null,
        lifeExpectancy: DEFAULT_LIFE_EXPECTANCY,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    };
  });

  // Global Settings State (Sound, Theme, Collection)
  const [settings, setSettings] = useState<AppSettings>(getInitialSettings);

  // IVT guard: start interaction watcher once on mount
  useEffect(() => {
    initInteractionWatch();
    recordPageView(window.location.pathname);
  }, []);

  // Resume AudioContext on first interaction to avoid "silent" start
  useEffect(() => {
    const resumeAudio = () => {
      initAudio();
      window.removeEventListener('click', resumeAudio);
      window.removeEventListener('touchstart', resumeAudio);
    };
    window.addEventListener('click', resumeAudio);
    window.addEventListener('touchstart', resumeAudio);
    return () => {
      window.removeEventListener('click', resumeAudio);
      window.removeEventListener('touchstart', resumeAudio);
    };
  }, []);

  // Apply Zodiac Season Theme + Mood Overlay
  useEffect(() => {
    const season = getCurrentSeasonSign();
    const theme = ZODIAC_THEMES[season];
    const root = document.documentElement;

    // Base Season Colors
    root.style.setProperty('--season-primary', theme.primary);
    root.style.setProperty('--season-secondary', theme.secondary);
    
    // Mood Overrides (affecting glow mainly)
    if (settings.currentMood && MOOD_THEMES[settings.currentMood]) {
        const moodTheme = MOOD_THEMES[settings.currentMood];
        root.style.setProperty('--season-glow', moodTheme.glow);
    } else {
        root.style.setProperty('--season-glow', theme.glow);
    }
  }, [settings.currentMood]);

  // Sync Data to Local Storage
  useEffect(() => {
    localStorage.setItem('zodiage_user', JSON.stringify(userData));
  }, [userData]);

  useEffect(() => {
    saveSettings(settings);
    // Apply night mode class for atmospheric visuals
    if (settings.nightMode) {
      document.documentElement.classList.add('night-mode');
    } else {
      document.documentElement.classList.remove('night-mode');
    }
  }, [settings]);

  // Route handling
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Keep canonical + og:url in sync on every SPA navigation.
  // The inline script in index.html handles the first load; this effect
  // keeps them correct on every client-side route change.
  useEffect(() => {
    const cleanPath = currentPath.length > 1 ? currentPath.replace(/\/+$/, '') : '/';
    const pageUrl = `https://zodiage.com${cleanPath}`;

    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonical) canonical.href = pageUrl;

    const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
    if (ogUrl) ogUrl.content = pageUrl;
  }, [currentPath]);

  const handleNavigate = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    window.scrollTo(0, 0);
    recordPageView(path);
  };

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const renderPage = () => {
    switch(currentPath) {
      case '/': return <Home userData={userData} setUserData={setUserData} settings={settings} updateSettings={updateSettings} />;
      case '/zodiac': return <ZodiacPage userData={userData} settings={settings} updateSettings={updateSettings} />;
      case '/progress': return <LifeProgress userData={userData} setUserData={setUserData} />;
      case '/personality': return <PersonalityPage userData={userData} settings={settings} updateSettings={updateSettings} />;
      case '/tarot': return <TarotPage userData={userData} settings={settings} updateSettings={updateSettings} />;
      case '/timeline': return <TimelinePage userData={userData} settings={settings} updateSettings={updateSettings} />;
      case '/chinese-zodiage': return <ChineseZodiacPage userData={userData} />;
      case '/privacy': return <PrivacyPolicy />;
      case '/about': return <About />;
      case '/contact': return <Contact />;
      case '/terms': return <TermsOfService />;
      case '/editorial': return <EditorialPolicy />;
      case '/methodology': {
        window.history.replaceState({}, '', '/editorial');
        setCurrentPath('/editorial');
        return <EditorialPolicy />;
      }
      case '/methodology-tools': return <MethodologyToolsPage />;
      case '/glossary': return <GlossaryPage />;
      case '/resources': return <ResourcesPage />;
      case '/sitemap': return <SitemapPage />;
      case '/guide': return <GettingStartedPage />;
      case '/faq': return <FAQPage />;
      case '/blog': return <BlogIndex />;
      default: 
        if (currentPath.startsWith('/blog/')) {
            const slug = currentPath.split('/blog/')[1];
            return <BlogPost slug={slug} />;
        }
        return <Home userData={userData} setUserData={setUserData} settings={settings} updateSettings={updateSettings} />;
    }
  };

  return (
    <>
      <Layout
        currentPath={currentPath}
        onNavigate={handleNavigate}
        settings={settings}
        updateSettings={updateSettings}
      >
        {renderPage()}
      </Layout>
      <CookieConsent />
    </>
  );
};

export default App;